import { createFileRoute, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { useLingui } from '@lingui/react/macro'
import { UserIcon, BuildingIcon, BriefcaseIcon, CalendarIcon, CheckCircleIcon } from 'lucide-react'
import { formatDateLocale } from '@/lib/utils'
import { useTranslation } from '@/lib/locales'
import { useRootSearch } from '@/hooks/use-root-search'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { SearchHeader } from './-components/api/search-header'
import { CompletionIndicator } from './-components/shared/completion-indicator'
import { Pagination } from './-components/shared/pagination'
import { SimpleListPending } from './-components/app-layout-skeleton'

export const Route = createFileRoute('/_app/account/api/$appId/jobs')({
  component: RouteComponent,
  pendingComponent: SimpleListPending,
  // Painted on the click frame; safe only because the searched list key lives on the pathless `_list` layout below.
})

function RouteComponent() {
  const { language } = useTranslation()
  const { t } = useLingui()
  const { q } = useRootSearch()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: q })

  const { context } = useMatch({ from: '/_app/account/api/$appId/jobs/_list' })
  // Defer the OPTIONS object (the context is reference-stable per match), so a new search keeps the
  // previous page on screen while the next one loads instead of flashing the skeleton.
  const deferredOptions = useDeferredValue(context.appJobsQueryOptions)
  const { data: offers, isFetching } = useSuspenseQuery(deferredOptions)
  // True while the deferred options lag the live ones — keeps the dimming through that transition.
  const isStale = isFetching || deferredOptions !== context.appJobsQueryOptions

  return (
    <div className="flex flex-col gap-4">
      <SearchHeader query={searchInput} onQueryChange={handleSearchChange} count={offers.pagination.count} />

      <div className="flex flex-col gap-3" style={{ opacity: isStale ? 0.6 : undefined }}>
        {offers.list.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <BriefcaseIcon className="text-muted-foreground/50 size-12" />
            <p className="text-muted-foreground">{t`No offers found`}</p>
          </div>
        )}

        {offers.list.map((elem) => {
          const { applicants, applicantsAddedMe } = elem
          return (
            <div key={elem.id} className="bg-card rounded-lg border p-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{elem.name ?? t`Unnamed`}</h3>
                    <code className="text-muted-foreground bg-muted rounded px-1.5 py-0.5 text-xs">{elem.id}</code>
                    <CompletionIndicator
                      skills={{ completed: elem.described, standard: elem.standard }}
                      values={{ completed: elem.describedValues, standard: elem.standardValues }}
                      traits={{ completed: elem.describedPersonality, standard: elem.standardPersonality }}
                      size="md"
                    />
                    {elem.fullyRegistered && <CheckCircleIcon className="size-3.5 text-emerald-500" />}
                  </div>

                  {elem.created && (
                    <div className="text-muted-foreground flex flex-col gap-1 text-xs sm:items-end">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="size-3" />
                        {t`Created`}: {formatDateLocale(elem.created, language)}
                      </span>
                    </div>
                  )}
                </div>

                {elem.company && (
                  <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
                    <span className="flex items-center gap-1">
                      <BuildingIcon className="size-3.5" />
                      {elem.company}
                    </span>
                  </div>
                )}

                <div className="text-muted-foreground flex items-center gap-4 border-t pt-2 text-sm">
                  <span className="flex items-center gap-1">
                    <UserIcon className="size-3.5" />
                    {applicants === 1 ? t`${applicants} candidate` : t`${applicants} applicants`}
                  </span>
                  <span className="flex items-center gap-1">{t`${applicantsAddedMe} added`}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <Pagination pagination={offers.pagination} />
    </div>
  )
}
